import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Search, User, X } from "lucide-react";
import InputCst from "@components/InputCst/InputCst";
import ListItemCardCst from "@components/ListItemCardCst";
import PaginatorCst from "@components/PaginatorCst";
import { usePaginatedList } from "@hooks/usePaginatedList";
import { useFetchDataDetail } from "@hooks/useFetchDetail";
import { ServicesURL } from "@utils/utilsApi";
import { openToast } from "@utils/toastStore";
import { updateUserStaticGroups } from "../updateUserStaticGroups";

const PAGE_SIZE = 10;

interface AddToStaticGroupsModalProps {
  open: boolean;
  onClose: () => void;
  corporateId: string;
  userId: string | number;
  userName: string;
  onSuccess?: () => void;
}

interface ContentProps {
  onClose: () => void;
  corporateId: string;
  userId: string | number;
  userName: string;
  onSuccess?: () => void;
}

const AddToStaticGroupsModalContent: React.FC<ContentProps> = ({
  onClose,
  corporateId,
  userId,
  userName,
  onSuccess,
}) => {
  const { t } = useTranslation("users-groups-v2");
  const theme = useTheme();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { dataDetail: userGroups, isLoading: isLoadingCurrent } =
    useFetchDataDetail(
      corporateId && userId
        ? `${ServicesURL.corporate}/${corporateId}/users/${userId}/groups?type=STATIC`
        : null,
    );

  const initialIds = useMemo(() => {
    const list = Array.isArray(userGroups)
      ? userGroups
      : userGroups?.groups_list || [];
    return list
      .map((g: any) => Number(g?.id))
      .filter((id: number) => !Number.isNaN(id));
  }, [userGroups]);

  const listUrl = corporateId
    ? `${ServicesURL.staticGroups}/static?id_corporate=${corporateId}`
    : null;

  const { items, totalItems, totalPages, isLoading } = usePaginatedList(
    listUrl,
    {
      page,
      pageSize,
      search: search || undefined,
    },
  );

  const toggle = (groupId: number, next: boolean) => {
    setSelected((prev) => {
      const copy = new Set(prev);
      if (next) copy.add(groupId);
      else copy.delete(groupId);
      return copy;
    });
  };

  const addGroupIds = useMemo(
    () => Array.from(selected).filter((id) => !initialIds.includes(id)),
    [initialIds, selected],
  );

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      await updateUserStaticGroups(userId, {
        addGroupIds,
        removeGroupIds: [],
      });
      onSuccess?.();
      onClose();
      openToast({
        severity: "success",
        message: String(t("add-to-groups-modal.success")),
      });
    } catch (e: any) {
      onClose();
      openToast({
        severity: "error",
        message: e?.message || String(t("add-to-groups-modal.error")),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <DialogTitle sx={{ padding: "20px 24px 0" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                color: theme.palette.text.primary,
              }}
            >
              <User size={16} />
              <Typography sx={{ fontSize: "13px" }}>{userName}</Typography>
            </Box>
            <Typography
              sx={{
                fontSize: "20px",
                fontWeight: 700,
                color: theme.palette.text.primary,
                mt: 0.5,
              }}
            >
              {t("add-to-groups-modal.title")}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            aria-label="close"
            disabled={isSubmitting}
            sx={{ color: theme.palette.text.primary, flexShrink: 0 }}
          >
            <X size={20} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: "0 24px 8px",
          "& .MuiOutlinedInput-root": { borderRadius: "8px" },
          "& .MuiFormHelperText-root": { display: "none" },
          "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
        }}
      >
        <Box
          sx={{ pt: "16px", display: "flex", flexDirection: "column", gap: 2 }}
        >
          <InputCst
            id="add-to-groups-search"
            label={t("add-to-groups-modal.search") || ""}
            value={searchInput}
            onChange={(e: any) => setSearchInput(e.target.value)}
            onKeyDown={(e: any) => {
              if (e.key === "Enter") {
                setSearch(searchInput);
                setPage(1);
              }
            }}
            endAdornment={
              <Search size={16} color={theme.palette.text.tertiary} />
            }
          />

          <Typography
            sx={{ fontSize: "13px", color: theme.palette.text.primary }}
          >
            {t("add-to-groups-modal.selected-count", {
              count: selected.size,
            })}
          </Typography>

          {isLoading || isLoadingCurrent ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
              <CircularProgress />
            </Box>
          ) : items.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                py: 5,
                color: theme.palette.text.primary,
              }}
            >
              {t("add-to-groups-modal.empty")}
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {items.map((group: any) => {
                const alreadyAssociated = initialIds.includes(Number(group.id));
                return (
                  <ListItemCardCst
                    key={group.id}
                    title={group.name || "-"}
                    subtitle={{ id: group.id }}
                    badges={
                      alreadyAssociated
                        ? [
                            {
                              label: String(
                                t("add-to-groups-modal.already-associated"),
                              ),
                              variant: "default",
                            },
                          ]
                        : []
                    }
                    actions={[]}
                    selectable
                    selected={
                      alreadyAssociated || selected.has(Number(group.id))
                    }
                    selectDisabled={alreadyAssociated || isLoadingCurrent}
                    onSelect={(next) => toggle(Number(group.id), next)}
                  />
                );
              })}
            </Box>
          )}

          {totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <PaginatorCst
                page={page}
                pageSize={pageSize}
                totalItems={totalItems}
                onPageChange={setPage}
                onPageSizeChange={(next) => {
                  setPageSize(next);
                  setPage(1);
                }}
              />
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ padding: "12px 24px 20px" }}>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={isSubmitting || addGroupIds.length === 0}
          sx={{ textTransform: "none", borderRadius: "8px", fontWeight: 700 }}
        >
          {isSubmitting
            ? t("add-to-groups-modal.submitting")
            : t("add-to-groups-modal.confirm")}
        </Button>
      </DialogActions>
    </>
  );
};

const AddToStaticGroupsModal: React.FC<AddToStaticGroupsModalProps> = ({
  open,
  onClose,
  corporateId,
  userId,
  userName,
  onSuccess,
}) => {
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      {open && (
        <AddToStaticGroupsModalContent
          onClose={onClose}
          corporateId={corporateId}
          userId={userId}
          userName={userName}
          onSuccess={onSuccess}
        />
      )}
    </Dialog>
  );
};

export default AddToStaticGroupsModal;
